import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, RefreshCw, Save, Download } from 'lucide-react';
import { Button, AlertMessage, PageHeader } from '../common/ui';
import GkPreview from './GkPreview';
import GkHistory from './GkHistory';
import GkDrift from './GkDrift';
import { GkDecisions } from './GkResolve';
import { formatCents, formatRate } from './GkPreview';
import type { GkStatus, GkUpload, GkUploadReport, GkPartnerKontoRow, GkRateDay, GkStatementReport, GkStatementFile } from './types';

/**
 * Vasco GK export page.
 * - Preview (M7): day documents, lines and exceptions for a range.
 * - Bank statement upload (M8): camt.053 for the control of credits per day.
 * - Export and history (M9): file, control file, confirm import, cancel.
 * - Decisions (M10): "Resolve" at an exception, list of open decisions.
 * - Changes after export (M11): drift check and correction (storno + replacement).
 * - GK export upload (M2): partner blocks, invoice postings, partner -> konto map.
 * - Bank of Slovenia rates for USD (M4).
 */

const inputClass =
  'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelClass = 'mb-1 text-xs font-medium text-gray-500';

function todayIso(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Ljubljana' });
}

function firstOfMonthIso(): string {
  return `${todayIso().slice(0, 8)}01`;
}

/** The file gzip-compressed when the browser can do it, else as it is. */
async function gzipFile(file: File): Promise<{ blob: Blob; name: string }> {
  const CS = (window as any).CompressionStream;
  if (!CS || typeof file.stream !== 'function') return { blob: file, name: file.name };
  const stream = file.stream().pipeThrough(new CS('gzip'));
  return { blob: await new Response(stream).blob(), name: `${file.name}.gz` };
}

function errorText(err: any, fallback: string): string {
  return err?.response?.data?.error || fallback;
}

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {subtitle && <p className="mb-3 mt-0.5 text-sm text-gray-500">{subtitle}</p>}
    {children}
  </section>
);

const UploadReportView: React.FC<{ report: GkUploadReport }> = ({ report }) => (
  <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
    <div>
      {report.lines} lines, posting dates {report.postingDates.from} to {report.postingDates.to}.
    </div>
    <div>
      Partners: {report.partners.inFile} in the file, {report.partners.new} new,{' '}
      {report.partners.changed.length} changed, {report.partners.unchanged} unchanged
      {report.partners.keptNewer.length > 0 && `, ${report.partners.keptNewer.length} kept because a newer upload has them`}.
    </div>
    <div>
      Invoice postings: {report.postings.inFile} in the file, {report.postings.new} new, {report.postings.updated} updated
      {report.postings.keptNewer > 0 && `, ${report.postings.keptNewer} kept from a newer upload`}.
    </div>
    {report.partners.changed.length > 0 && (
      <div>Changed partner blocks: {report.partners.changed.map((p) => `${p.sifra} ${p.naziv1 || ''}`.trim()).join(', ')}.</div>
    )}
    {report.invalidBlocks.length > 0 && (
      <div className="text-red-700">
        Not stored (damaged partner data): {report.invalidBlocks.map((b) => `${b.sifra} (${b.reason})`).join(', ')}.
      </div>
    )}
    {report.postings.conflicts.length > 0 && (
      <div className="text-amber-700">
        Conflicts: {report.postings.conflicts.map((c) => `${c.key}: ${c.reason}`).join('; ')}.
      </div>
    )}
    {report.letoVezaDiffers.length > 0 && (
      <div>
        GK holds another year for: {report.letoVezaDiffers.map((d) => `${d.invoice} (Leto_veza ${d.gkLetoVeza})`).join(', ')}.
      </div>
    )}
    {report.invoicesWithoutPosting && (
      <div>
        Vasco invoices since the start date that this GK data does not contain yet: {report.invoicesWithoutPosting.count}
        {report.invoicesWithoutPosting.count > 0 && ` (first: ${report.invoicesWithoutPosting.first.slice(0, 10).join(', ')})`}.
      </div>
    )}
  </div>
);

const GkExport: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<GkStatus | null>(null);
  const [pageError, setPageError] = useState('');
  const [historyKey, setHistoryKey] = useState(0);
  const [decisionsKey, setDecisionsKey] = useState(0);

  // GK export upload
  const [file, setFile] = useState<File | null>(null);
  const [fileKind, setFileKind] = useState<'gk_export' | 'faw'>('gk_export');
  const [exportedAt, setExportedAt] = useState(todayIso());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadReport, setUploadReport] = useState<GkUploadReport | null>(null);
  const [uploads, setUploads] = useState<GkUpload[]>([]);

  // Bank statement
  const [stmtFile, setStmtFile] = useState<File | null>(null);
  const [stmtUploading, setStmtUploading] = useState(false);
  const [stmtError, setStmtError] = useState('');
  const [stmtReport, setStmtReport] = useState<GkStatementReport | null>(null);
  const [statements, setStatements] = useState<GkStatementFile[]>([]);
  const [stmtFrom, setStmtFrom] = useState(firstOfMonthIso());
  const [stmtTo, setStmtTo] = useState(todayIso());
  const [stmtFetching, setStmtFetching] = useState(false);
  const [stmtFetchMessage, setStmtFetchMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Partner -> konto map
  const [kontoRows, setKontoRows] = useState<GkPartnerKontoRow[]>([]);
  const [kontoSifra, setKontoSifra] = useState('');
  const [kontoValue, setKontoValue] = useState('');
  const [kontoRule, setKontoRule] = useState<'none' | 'order_currency'>('none');
  const [kontoMessage, setKontoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rates
  const [rateFrom, setRateFrom] = useState(firstOfMonthIso());
  const [rateTo, setRateTo] = useState(todayIso());
  const [rates, setRates] = useState<GkRateDay[]>([]);
  const [fetchingRates, setFetchingRates] = useState(false);
  const [rateMessage, setRateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [manualDay, setManualDay] = useState('');
  const [manualRate, setManualRate] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const [s, u, k, st] = await Promise.all([
        axios.get<GkStatus>('/api/accounting/gk-export/status'),
        axios.get<{ uploads: GkUpload[] }>('/api/accounting/gk-export/uploads'),
        axios.get<{ rows: GkPartnerKontoRow[] }>('/api/accounting/gk-export/partner-konto'),
        axios.get<{ statements: GkStatementFile[] }>('/api/accounting/gk-export/statements'),
      ]);
      setStatus(s.data);
      setUploads(u.data.uploads);
      setKontoRows(k.data.rows);
      setStatements(st.data.statements || []);
    } catch (err) {
      setPageError(errorText(err, 'Could not load the GK export data.'));
    }
  }, []);

  const loadRates = useCallback(async (from: string, to: string) => {
    if (!from || !to || from > to) return;
    try {
      const res = await axios.get<{ days: GkRateDay[] }>('/api/accounting/gk-export/rates', { params: { from, to, currency: 'USD' } });
      // The server lists the days oldest first; the table shows the newest first.
      setRates([...res.data.days].reverse());
    } catch (err) {
      setRateMessage({ type: 'error', text: errorText(err, 'Could not load the rates.') });
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadRates(rateFrom, rateTo);
  }, [loadRates, rateFrom, rateTo]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    setUploadReport(null);
    try {
      const form = new FormData();
      // A year of GK is about 30 MB; gzip makes it about 1 MB for the upload.
      const packed = await gzipFile(file);
      form.append('file', packed.blob, packed.name);
      form.append('fileKind', fileKind);
      form.append('exportedAt', exportedAt);
      const res = await axios.post<{ report: GkUploadReport }>('/api/accounting/gk-export/partners/upload', form);
      setUploadReport(res.data.report);
      setFile(null);
      await loadStatus();
    } catch (err) {
      setUploadError(errorText(err, 'Upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  const handleStatementUpload = async () => {
    if (!stmtFile) return;
    setStmtUploading(true);
    setStmtError('');
    setStmtReport(null);
    try {
      const form = new FormData();
      form.append('file', stmtFile);
      const res = await axios.post<GkStatementReport>('/api/accounting/gk-export/statements/upload', form);
      setStmtReport(res.data);
      setStmtFile(null);
      await loadStatus();
    } catch (err) {
      setStmtError(errorText(err, 'Statement upload failed.'));
    } finally {
      setStmtUploading(false);
    }
  };

  const handleStatementFetch = async () => {
    setStmtFetching(true);
    setStmtFetchMessage(null);
    try {
      const res = await axios.post<{ statements: Array<{ currency: string; entries: number; newEntries: number }> }>(
        '/api/accounting/gk-export/statements/fetch',
        { from: stmtFrom, to: stmtTo }
      );
      const parts = res.data.statements.map((s) => `${s.currency}: ${s.entries} entries, ${s.newEntries} new`);
      setStmtFetchMessage({ type: 'success', text: `Read from Revolut. ${parts.join('; ')}.` });
      await loadStatus();
    } catch (err) {
      setStmtFetchMessage({ type: 'error', text: errorText(err, 'Could not read the statement from Revolut.') });
    } finally {
      setStmtFetching(false);
    }
  };

  const handleSaveKonto = async () => {
    setKontoMessage(null);
    try {
      await axios.put(`/api/accounting/gk-export/partner-konto/${kontoSifra}`, { konto: kontoValue, sifraValuteRule: kontoRule });
      setKontoMessage({ type: 'success', text: `Partner ${kontoSifra} now uses konto ${kontoValue}.` });
      setKontoSifra('');
      setKontoValue('');
      await loadStatus();
    } catch (err) {
      setKontoMessage({ type: 'error', text: errorText(err, 'Could not save the konto.') });
    }
  };

  const handleFetchRates = async () => {
    setFetchingRates(true);
    setRateMessage(null);
    try {
      const res = await axios.post('/api/accounting/gk-export/rates/fetch', { from: rateFrom, to: rateTo, currency: 'USD' });
      const covered = res.data.coveredTo ? `, published up to ${res.data.coveredTo}` : '';
      setRateMessage({ type: 'success', text: `Stored ${res.data.stored} Bank of Slovenia rates${covered}.` });
      await Promise.all([loadRates(rateFrom, rateTo), loadStatus()]);
    } catch (err) {
      setRateMessage({ type: 'error', text: errorText(err, 'Could not fetch the rates.') });
    } finally {
      setFetchingRates(false);
    }
  };

  const handleManualRate = async () => {
    setRateMessage(null);
    try {
      await axios.put(`/api/accounting/gk-export/rates/${manualDay}`, { currency: 'USD', rate: manualRate });
      setRateMessage({ type: 'success', text: `Manual rate ${manualRate} saved for ${manualDay}.` });
      setManualDay('');
      setManualRate('');
      await loadRates(rateFrom, rateTo);
    } catch (err) {
      setRateMessage({ type: 'error', text: errorText(err, 'Could not save the manual rate.') });
    }
  };

  return (
    <div className="p-6 pb-20">
      <PageHeader title="Vasco GK export" subtitle="Receipts on the Revolut statement, as journal entries for Vasco GK" />

      <div className="mb-4">
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/accounting')}>
          Back to accounting
        </Button>
      </div>

      {pageError && (
        <AlertMessage type="error" message={pageError} title="" onDismiss={() => setPageError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      {status && !status.enabled && (
        <AlertMessage type="warning" message="The GK export is switched off on this server (GK_EXPORT_ENABLED). Uploads and rate downloads are not possible." title="">
          {''}
        </AlertMessage>
      )}

      {status && (
        <div className="mb-6 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Start date</div>
            <div className="font-medium">{status.startDate}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Last GK upload</div>
            <div className="font-medium">{status.lastUpload ? `${status.lastUpload.filename} (${status.lastUpload.exported_at})` : 'none'}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Partners / invoice postings</div>
            <div className="font-medium tabular-nums">
              {status.partners} / {status.postings}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Newest USD rate</div>
            <div className="font-medium">{status.newestUsdRate || 'none'}</div>
          </div>
        </div>
      )}

      {status && (
        <Section
          title="Export preview"
          subtitle="Journal entries for the receipts on the Revolut statement, one document per statement day. A day with an exception is held; the other days can be exported."
        >
          <GkPreview
            startDate={status.startDate}
            enabled={status.enabled}
            onExported={() => setHistoryKey((k) => k + 1)}
            onDecided={() => setDecisionsKey((k) => k + 1)}
          />
        </Section>
      )}

      {status && (
        <Section
          title="Decisions"
          subtitle="How held items are booked: an advance, a konto, an invoice, or a refund on an invoice. A decision counts at the next preview. It can be removed until its payment is exported."
        >
          <GkDecisions refreshKey={decisionsKey} enabled={status.enabled} />
        </Section>
      )}

      {status && (
        <Section
          title="Changes after export"
          subtitle="Exported documents are built again from the current data. A document whose payments changed is listed here."
        >
          <GkDrift
            enabled={status.enabled}
            closedThrough={status.closedThrough}
            autoCheck={Boolean(status.lastExports && Object.keys(status.lastExports).length)}
            onCorrected={() => setHistoryKey((k) => k + 1)}
          />
        </Section>
      )}

      {status && (
        <Section
          title="Export history"
          subtitle="Every export with its XML file and the control file for AJMP. Confirm when AJMP has imported the file. Cancel only when the journal is not in GK (AJMP deleted it, or it was never imported)."
        >
          <GkHistory refreshKey={historyKey} enabled={status.enabled} />
        </Section>
      )}

      <Section
        title="Bank statement"
        subtitle="The statement of the Revolut Main account. The credits of each day must equal the credits in the app. The preview reads missing days from the Revolut API by itself; you can also read a period here or upload a camt.053 file."
      >
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="stmt-from" className={labelClass}>From</label>
            <input id="stmt-from" type="date" value={stmtFrom} max={stmtTo || undefined} onChange={(e) => setStmtFrom(e.target.value)} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label htmlFor="stmt-to" className={labelClass}>To</label>
            <input id="stmt-to" type="date" value={stmtTo} min={stmtFrom || undefined} onChange={(e) => setStmtTo(e.target.value)} className={inputClass} />
          </div>
          <Button variant="secondary" icon={Download} onClick={handleStatementFetch} disabled={stmtFetching || !stmtFrom || !stmtTo || stmtFrom > stmtTo || !status?.enabled}>
            {stmtFetching ? 'Reading…' : 'Read from Revolut'}
          </Button>
        </div>
        {stmtFetchMessage && (
          <p className={`mb-3 text-sm ${stmtFetchMessage.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{stmtFetchMessage.text}</p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="stmt-file" className={labelClass}>camt.053 file (.xml)</label>
            <input id="stmt-file" type="file" accept=".xml" onChange={(e) => setStmtFile(e.target.files?.[0] || null)} className="text-sm" />
          </div>
          <Button icon={Upload} onClick={handleStatementUpload} disabled={!stmtFile || stmtUploading || !status?.enabled}>
            {stmtUploading ? 'Uploading…' : 'Upload statement'}
          </Button>
        </div>
        {stmtError && <p className="mt-2 text-sm text-red-700">{stmtError}</p>}
        {stmtReport && (
          <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
            {stmtReport.statements.map((st) => (
              <div key={st.fileId}>
                {st.currency}: {st.entries} entries ({st.newEntries} new), credits {st.credits.count} · {formatCents(st.credits.cents)}, debits{' '}
                {st.debits.count} · {formatCents(st.debits.cents)}, balance {formatCents(st.openingCents)} → {formatCents(st.closingCents)}.{' '}
                {st.days.complete} complete days
                {st.days.partial.length > 0 && `; partial: ${st.days.partial.join(', ')}`}.
              </div>
            ))}
            {stmtReport.skipped.length > 0 && <div>Not used: {stmtReport.skipped.map((x) => x.currency).join(', ')}.</div>}
          </div>
        )}
        {statements.length > 0 && (
          <div className="mt-4 max-h-60 overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 pr-4">Uploaded</th>
                  <th className="py-1 pr-4">Source</th>
                  <th className="py-1 pr-4">Currency</th>
                  <th className="py-1 pr-4">Period (UTC)</th>
                  <th className="py-1 pr-4 text-right">Opening</th>
                  <th className="py-1 pr-4 text-right">Closing</th>
                  <th className="py-1 pr-4 text-right">New entries</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((st) => (
                  <tr key={st.id} className="border-t border-gray-100">
                    <td className="py-1 pr-4">{new Date(st.loaded_at).toLocaleString('sl-SI')}</td>
                    <td className="py-1 pr-4">{st.filename}</td>
                    <td className="py-1 pr-4">{st.currency}</td>
                    <td className="py-1 pr-4">{String(st.from_ts).slice(0, 10)} – {String(st.to_ts).slice(0, 10)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{formatCents(st.opening_cents)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{formatCents(st.closing_cents)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{st.new_entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="GK export upload"
        subtitle="Upload a journal export from Vasco GK (from AJMP) or a FAW transfer file. It provides the partner data and the receivable konto of each invoice."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="gk-file" className={labelClass}>File (.xml)</label>
            <input id="gk-file" type="file" accept=".xml" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          </div>
          <div className="flex flex-col">
            <label htmlFor="gk-kind" className={labelClass}>File kind</label>
            <select id="gk-kind" value={fileKind} onChange={(e) => setFileKind(e.target.value as 'gk_export' | 'faw')} className={inputClass}>
              <option value="gk_export">GK export (AJMP)</option>
              <option value="faw">FAW transfer file</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label htmlFor="gk-exported-at" className={labelClass}>Exported on</label>
            <input id="gk-exported-at" type="date" value={exportedAt} max={todayIso()} onChange={(e) => setExportedAt(e.target.value)} className={inputClass} />
          </div>
          <Button icon={Upload} onClick={handleUpload} disabled={!file || !exportedAt || uploading || !status?.enabled}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
        {uploadError && <p className="mt-2 text-sm text-red-700">{uploadError}</p>}
        {uploadReport && <UploadReportView report={uploadReport} />}

        {uploads.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 pr-4">Uploaded</th>
                  <th className="py-1 pr-4">File</th>
                  <th className="py-1 pr-4">Kind</th>
                  <th className="py-1 pr-4">Exported on</th>
                  <th className="py-1 pr-4">Posting dates</th>
                  <th className="py-1 pr-4 text-right">Lines</th>
                  <th className="py-1 pr-4">By</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="py-1 pr-4">{new Date(u.uploaded_at).toLocaleString('sl-SI')}</td>
                    <td className="py-1 pr-4">{u.filename}</td>
                    <td className="py-1 pr-4">{u.file_kind === 'faw' ? 'FAW' : 'GK export'}</td>
                    <td className="py-1 pr-4">{u.exported_at}</td>
                    <td className="py-1 pr-4">{u.min_posting_date} – {u.max_posting_date}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{u.line_count}</td>
                    <td className="py-1 pr-4">{u.uploaded_by_name || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Receivable konto per partner"
        subtitle="Used only when an invoice has no posting in the uploaded GK data. Rows from uploads are derived; a row you save here is manual and is never changed by an upload."
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="konto-sifra" className={labelClass}>Partner code</label>
            <input id="konto-sifra" value={kontoSifra} onChange={(e) => setKontoSifra(e.target.value.trim())} className={`${inputClass} w-32`} />
          </div>
          <div className="flex flex-col">
            <label htmlFor="konto-value" className={labelClass}>Konto</label>
            <input id="konto-value" value={kontoValue} onChange={(e) => setKontoValue(e.target.value.trim())} className={`${inputClass} w-28`} />
          </div>
          <div className="flex flex-col">
            <label htmlFor="konto-rule" className={labelClass}>Currency on the line</label>
            <select id="konto-rule" value={kontoRule} onChange={(e) => setKontoRule(e.target.value as 'none' | 'order_currency')} className={inputClass}>
              <option value="none">none</option>
              <option value="order_currency">order currency (121000)</option>
            </select>
          </div>
          <Button variant="secondary" icon={Save} onClick={handleSaveKonto} disabled={!kontoSifra || !kontoValue || !status?.enabled}>
            Save
          </Button>
        </div>
        {kontoMessage && <p className={`mb-2 text-sm ${kontoMessage.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{kontoMessage.text}</p>}
        <div className="max-h-80 overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-4">Partner</th>
                <th className="py-1 pr-4">Name</th>
                <th className="py-1 pr-4">Konto</th>
                <th className="py-1 pr-4">Currency</th>
                <th className="py-1 pr-4">Source</th>
              </tr>
            </thead>
            <tbody>
              {kontoRows.map((r) => (
                <tr key={r.partner_sifra} className="border-t border-gray-100">
                  <td className="py-1 pr-4 tabular-nums">{r.partner_sifra}</td>
                  <td className="py-1 pr-4">{r.naziv1 || r.note || ''}</td>
                  <td className="py-1 pr-4 tabular-nums">{r.konto}</td>
                  <td className="py-1 pr-4">{r.sifra_valute_rule === 'order_currency' ? 'order currency' : ''}</td>
                  <td className="py-1 pr-4">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="USD rates (Bank of Slovenia)"
        subtitle="Middle rates, USD per 1 EUR. Weekends and holidays use the last published rate. A manual rate wins over the published one. Sync on the Accounting page fetches the new rates up to today."
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="rate-from" className={labelClass}>From</label>
            <input id="rate-from" type="date" value={rateFrom} max={rateTo || undefined} onChange={(e) => setRateFrom(e.target.value)} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label htmlFor="rate-to" className={labelClass}>To</label>
            <input id="rate-to" type="date" value={rateTo} min={rateFrom || undefined} onChange={(e) => setRateTo(e.target.value)} className={inputClass} />
          </div>
          <Button variant="secondary" icon={RefreshCw} onClick={handleFetchRates} disabled={fetchingRates || !rateFrom || !rateTo || rateFrom > rateTo || !status?.enabled}>
            {fetchingRates ? 'Fetching…' : 'Fetch from Bank of Slovenia'}
          </Button>
          <div className="flex flex-col">
            <label htmlFor="manual-day" className={labelClass}>Manual rate: day</label>
            <input id="manual-day" type="date" value={manualDay} onChange={(e) => setManualDay(e.target.value)} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label htmlFor="manual-rate" className={labelClass}>USD per EUR</label>
            <input id="manual-rate" value={manualRate} onChange={(e) => setManualRate(e.target.value.trim())} className={`${inputClass} w-28`} />
          </div>
          <Button variant="secondary" icon={Save} onClick={handleManualRate} disabled={!manualDay || !manualRate || !status?.enabled}>
            Save manual rate
          </Button>
        </div>
        {rateMessage && <p className={`mb-2 text-sm ${rateMessage.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{rateMessage.text}</p>}
        <div className="max-h-80 overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-4">Day</th>
                <th className="py-1 pr-4 text-right">USD per EUR</th>
                <th className="py-1 pr-4">Source</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((d) => (
                <tr key={d.day} className="border-t border-gray-100">
                  <td className="py-1 pr-4">{d.day}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{d.rate ? formatRate(d.rate) : '–'}</td>
                  <td className="py-1 pr-4 text-gray-600">
                    {!d.rate && 'no rate yet'}
                    {d.rate && d.source === 'manual' && 'manual'}
                    {d.rate && d.source === 'bsi' && (d.fromEarlierDay ? `Bank of Slovenia, from ${d.rateDate}` : 'Bank of Slovenia')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

export default GkExport;
